import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Award, Shield, Users, CheckCircle, TrendingUp, Building2, Phone, Mail, MessageCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import LeadForm from '@/components/LeadForm';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProperties = async () => {
      try {
        const response = await api.get('/properties?featured=true');
        setFeaturedProperties(response.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProperties();
  }, []);

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Property Investor',
      content: 'PropVanta helped us find the perfect investment property in Mumbai. Their expertise and professionalism are outstanding.',
      rating: 5,
    },
    {
      name: 'Priya Sharma',
      role: 'First Home Buyer',
      content: 'The team made our first property purchase seamless. Highly recommend their services! Very professional and trustworthy.',
      rating: 5,
    },
    {
      name: 'Amit Desai',
      role: 'Luxury Home Buyer',
      content: 'Found our dream home through PropVanta. The attention to detail and customer service exceeded all expectations.',
      rating: 5,
    },
  ];

  const trustBadges = [
    {
      icon: Award,
      title: 'Trusted Channel Partner',
      description: 'Official channel partner with leading developers',
    },
    {
      icon: Shield,
      title: 'Verified Listings',
      description: '100% verified and authentic property listings',
    },
    {
      icon: Users,
      title: 'Expert Guidance',
      description: 'Professional real estate consultants at your service',
    },
    {
      icon: TrendingUp,
      title: 'Best Market Value',
      description: 'Competitive pricing and excellent ROI',
    },
  ];

  return (
    <div className="min-h-screen bg-dark" data-testid="home-page">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1714143150541-76715fb625d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMGFyY2hpdGVjdHVyZSUyMGV4dGVyaW9yJTIwbmlnaHR8ZW58MHx8fHwxNzczOTUyNTcxfDA&ixlib=rb-4.1.0&q=85"
            alt="Premium Property"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark/90 via-dark/70 to-dark/90" />
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-24 text-center max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-rose-gold/20 border border-rose-gold/50 backdrop-blur-sm text-rose-gold px-8 py-3 rounded-full text-sm font-semibold mb-8 uppercase tracking-wider">
              Your Trusted Channel Partner • Not a Marketplace
            </div>
            <h1 className="font-heading text-6xl md:text-8xl font-bold text-white mb-8 leading-none">
              Find Your
              <br />
              <span className="text-rose-gold">Dream Property</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-6 max-w-3xl mx-auto font-light">
              We are your dedicated channel partner connecting you with premium projects from leading builders.
            </p>
            <p className="text-lg text-white/60 mb-12 max-w-2xl mx-auto">
              Get expert guidance, free site visits, and personalized assistance throughout your property buying journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="/projects"
                data-testid="hero-explore-button"
                className="btn-primary inline-flex items-center justify-center gap-3 text-sm uppercase tracking-wider"
              >
                Explore Projects
                <ChevronRight size={20} />
              </a>
              <a
                href="#book-site-visit"
                data-testid="hero-site-visit-button"
                className="btn-outline inline-flex items-center justify-center text-sm uppercase tracking-wider"
              >
                Book Free Site Visit
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Channel Partner Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-dark to-dark-light">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">How We Help You</p>
            <h2 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Why Work with a <span className="text-rose-gold">Channel Partner?</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-3xl mx-auto">
              Unlike property marketplaces, we provide personalized, end-to-end assistance in finding and securing your ideal property. 
              As an official channel partner with leading builders, we offer exclusive access and expert guidance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-6 shadow-rose-glow">
                <Users size={36} className="text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">Personal Guidance</h3>
              <p className="text-white/60 leading-relaxed">One-on-one consultation tailored to your specific needs and budget. No automated responses.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-6 shadow-rose-glow">
                <Award size={36} className="text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">Exclusive Access</h3>
              <p className="text-white/60 leading-relaxed">Direct partnerships with builders mean better prices and exclusive pre-launch opportunities.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-6 shadow-rose-glow">
                <Shield size={36} className="text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">Complete Support</h3>
              <p className="text-white/60 leading-relaxed">From site visits to documentation and home loans - we handle everything for you.</p>
            </motion.div>
          </div>

          <div className="text-center">
            <a href="tel:+917020970161" className="btn-primary inline-flex items-center gap-3 text-sm uppercase tracking-wider">
              <Phone size={20} />
              Call Us Now for Free Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-20 bg-dark">
        <div className="px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Your Trusted <span className="text-rose-gold">Real Estate Partner</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {trustBadges.map((badge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-6 shadow-rose-glow group-hover:scale-110 transition-transform duration-300">
                  <badge.icon className="text-white" size={36} strokeWidth={2} />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-3">{badge.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{badge.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information Banner */}
      <section className="py-16 bg-gradient-to-r from-rose-gold/20 to-rose-dark/20 border-y border-rose-gold/30">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
                Ready to Find Your Dream Home?
              </h2>
              <p className="text-white/80 text-lg">
                Contact us directly for personalized property recommendations
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.a
                href="tel:+917020970161"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="glass-effect p-8 rounded-2xl border-2 border-rose-gold/50 hover:border-rose-gold hover:shadow-rose-glow transition-all group"
              >
                <Phone className="text-rose-gold mb-4 group-hover:scale-110 transition-transform" size={40} />
                <p className="text-white/60 text-sm mb-2 uppercase tracking-wider">Call Now</p>
                <p className="text-white text-2xl font-bold">+91 70209 70161</p>
              </motion.a>

              <motion.a
                href="https://wa.me/917020970161"
                target="__blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="glass-effect p-8 rounded-2xl border-2 border-green-500/50 hover:border-green-500 hover:shadow-lg transition-all group"
              >
                <MessageCircle className="text-green-500 mb-4 group-hover:scale-110 transition-transform" size={40} />
                <p className="text-white/60 text-sm mb-2 uppercase tracking-wider">WhatsApp</p>
                <p className="text-white text-xl font-bold">Message Us Instantly</p>
              </motion.a>

              <motion.a
                href="mailto:info@propvanta.co.in"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="glass-effect p-8 rounded-2xl border-2 border-rose-gold/50 hover:border-rose-gold hover:shadow-rose-glow transition-all group"
              >
                <Mail className="text-rose-gold mb-4 group-hover:scale-110 transition-transform" size={40} />
                <p className="text-white/60 text-sm mb-2 uppercase tracking-wider">Email</p>
                <p className="text-white text-lg font-bold">info@propvanta.co.in</p>
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-32 px-6 md:px-12 lg:px-24 bg-dark" data-testid="featured-properties-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Handpicked Selection</p>
          <h2 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Featured Projects
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Explore our curated collection of premium projects from trusted builders
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center text-white/60">Loading projects...</div>
        ) : featuredProperties.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
            <div className="text-center">
              <a
                href="/projects"
                data-testid="view-all-projects-button"
                className="btn-outline inline-block text-sm uppercase tracking-wider"
              >
                View All Projects
              </a>
            </div>
          </>
        ) : (
          <div className="text-center text-white/60">No featured projects available at the moment.</div>
        )}
      </section>

      {/* Why Choose PropVanta - Personal */}
      <section className="py-32 bg-gradient-to-b from-dark-light to-dark">
        <div className="px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">About PropVanta</p>
            <h2 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6">
              Your Personal Real Estate Guide
            </h2>
            <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
              We're not just showing properties - we're your dedicated partner in making the right investment decision. 
              With direct partnerships with leading builders, we provide honest guidance and exclusive access.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {[
              {
                title: 'Direct Builder Partnerships',
                description: 'Official channel partner with Lodha, Rustomjee, and other premium developers. Get the best prices and pre-launch access.',
                icon: Building2,
              },
              {
                title: 'Personal Assistance',
                description: 'We personally accompany you on site visits and guide you through every decision - from selection to possession.',
                icon: Users,
              },
              {
                title: 'Complete Transparency',
                description: 'No hidden charges, no commissions from you. Clear pricing and honest recommendations always.',
                icon: Shield,
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-10 rounded-2xl bg-dark-light border border-dark-lighter hover:border-rose-gold/50 transition-all group"
              >
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-8 shadow-rose-glow group-hover:scale-110 transition-transform">
                  <feature.icon size={40} strokeWidth={2} className="text-white" />
                </div>
                <h3 className="font-heading text-3xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-white/60 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="/about"
              className="btn-outline inline-block text-sm uppercase tracking-wider"
            >
              Know More About Us
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 px-6 md:px-12 lg:px-24 bg-dark">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Client Reviews</p>
          <h2 className="font-heading text-5xl md:text-7xl font-bold text-white">
            What Our Clients Say
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-effect p-10 rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <div className="flex gap-1 mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} className="text-rose-gold text-2xl">★</span>
                ))}
              </div>
              <p className="text-white/80 leading-relaxed mb-8 italic text-lg">"{testimonial.content}"</p>
              <div>
                <p className="text-white font-semibold text-lg">{testimonial.name}</p>
                <p className="text-white/40 text-sm uppercase tracking-wider mt-1">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Book Free Site Visit */}
      <section id="book-site-visit" className="py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-dark-light to-dark">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Personal Service</p>
            <h2 className="font-heading text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              We'll Personally Take You
              <br />
              <span className="text-rose-gold">On Site Visits</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              Not just arranging visits - our team personally accompanies you to properties, explains all details, 
              and helps you make informed decisions. This is what sets a channel partner apart from marketplaces.
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={24} />
                <p className="text-white/80 text-lg">We personally accompany you on every site visit</p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={24} />
                <p className="text-white/80 text-lg">Expert guidance on property selection and pricing</p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={24} />
                <p className="text-white/80 text-lg">Complete support from booking to possession</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <LeadForm leadType="site_visit" title="Book Your Site Visit" />
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
